import type { StyleSpecification } from "maplibre-gl";
import { HAS_TRAFFIC_PROXY, nrwBasemapTileUrl } from "./trafficNrw";

export type MapStyleKey =
  | "voyager"
  | "positron"
  | "darkMatter"
  | "osmStandard"
  | "osmHot"
  | "openTopo"
  | "verkehrNrw";

const OSM_ATTRIBUTION = '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
/** MapLibre insists on a glyph source even for a style that writes nothing. */
const GLYPHS = "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf";

/**
 * A style for a plain tile server.
 *
 * The three OpenStreetMap-based maps serve finished images rather than vector data, and MapLibre
 * needs a style around them before it will show them.
 */
function rasterStyle(name: string, tiles: string[], attribution: string): StyleSpecification {
  return {
    version: 8,
    glyphs: GLYPHS,
    sources: {
      [name]: { type: "raster", tiles, tileSize: 256, attribution },
    },
    layers: [{ id: name, type: "raster", source: name, minzoom: 0, maxzoom: 22 }],
  };
}

/**
 * The basemaps a reader can choose between.
 *
 * `voyager` is what the map has always used and stays the default. The three OpenStreetMap ones
 * are here because they show smaller roads and yard detail that the vector styles leave out,
 * which is what a depot looks for.
 */
export const MAP_STYLES: Record<MapStyleKey, string | StyleSpecification> = {
  voyager: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
  positron: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
  darkMatter: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
  osmStandard: rasterStyle(
    "osm-standard",
    ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
    OSM_ATTRIBUTION,
  ),
  osmHot: rasterStyle(
    "osm-hot",
    ["https://a.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"],
    `${OSM_ATTRIBUTION}, tiles by the Humanitarian OpenStreetMap Team`,
  ),
  openTopo: rasterStyle(
    "open-topo",
    ["https://a.tile.opentopomap.org/{z}/{x}/{y}.png"],
    `${OSM_ATTRIBUTION}, style: OpenTopoMap (CC-BY-SA)`,
  ),
  verkehrNrw: {
    version: 8,
    glyphs: GLYPHS,
    sources: {
      "verkehr-nrw-base": {
        type: "raster",
        tiles: [nrwBasemapTileUrl("osm_tiles")],
        tileSize: 256,
        attribution: `${OSM_ATTRIBUTION}, style: Straßen.NRW (verkehr.nrw)`,
      },
      // The labels come as their own layer so they stay on top of the roads.
      "verkehr-nrw-labels": {
        type: "raster",
        tiles: [nrwBasemapTileUrl("osm_tiles_foreground")],
        tileSize: 256,
      },
    },
    layers: [
      { id: "verkehr-nrw-base", type: "raster", source: "verkehr-nrw-base", minzoom: 0, maxzoom: 22 },
      {
        id: "verkehr-nrw-labels",
        type: "raster",
        source: "verkehr-nrw-labels",
        minzoom: 0,
        maxzoom: 22,
      },
    ],
  },
};

/**
 * In the order they are offered, vector styles first.
 *
 * The NRW map only appears where the proxy that fetches it exists; on the statically published
 * build it is not offered at all, rather than offered and broken.
 */
export const MAP_STYLE_KEYS: MapStyleKey[] = [
  "voyager",
  "positron",
  "darkMatter",
  "osmStandard",
  "osmHot",
  "openTopo",
  ...(HAS_TRAFFIC_PROXY ? (["verkehrNrw"] as const) : []),
];
